// Schedules — 真實資料版（Route 10, PR #100）。
//
// Two tabs:
//   - 班別 (shifts) — recurring template CRUD（PR #100）
//   - 月排班 (roster) — per-day per-officer-per-site assignments（Phase 1B）
//
// Days mask on shifts: bit 0 = Mon ... bit 6 = Sun.

import React, { useCallback, useEffect, useState } from "react";
import { Plus, RefreshCw, ServerCrash, CalendarDays, Building2, Trash2, Users } from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import Drawer from "../components/Drawer.js";
import { Field } from "../components/Field.js";
import {
  listShifts, createShift, patchShift, archiveShift, friendlyShiftsError,
  type ShiftRow, type ShiftStatus,
} from "../lib/shiftsApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";
import RosterCalendar from "./RosterCalendar.js";
import SwapRequests from "./SwapRequests.js";
import MonthlyHours from "./MonthlyHours.js";

type Tab = "shifts" | "roster" | "swaps" | "hours";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const DAY_LABEL: Record<typeof DAYS[number], string> = { Mon: "一", Tue: "二", Wed: "三", Thu: "四", Fri: "五", Sat: "六", Sun: "日" };
const STATUS_LABEL: Record<ShiftStatus, string> = { active: "啟用", inactive: "停用" };
const STATUS_BADGE: Record<ShiftStatus, string> = { active: "badge-success", inactive: "badge-default" };

function maskHas(mask: number, idx: number): boolean { return (mask & (1 << idx)) !== 0; }
function maskToggle(mask: number, idx: number): number { return mask ^ (1 << idx); }

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: ShiftRow[] }
  | { status: "error"; message: string };
type DrawerState = null | { mode: "new" } | { mode: "edit"; shift: ShiftRow };

export default function Schedules(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite = session.scopes.includes("write:schedule") || session.super_admin === true;
  const [tab, setTab] = useState<Tab>("roster");
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [editing, setEditing] = useState<DrawerState>(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listShifts(tenantParam);
      setState({ status: "ready", rows: r.items });
    } catch (err) { setState({ status: "error", message: friendlyShiftsError(err) }); }
  }, [tenantParam]);
  useEffect(() => { if (tab === "shifts") void load(); }, [load, tab]);

  if (session.super_admin && !tenantParam) {
    return (<><PageHeader title="班表" description="班別與排班規劃" />
      <TenantPickerEmptyState /></>);
  }

  const tabBtn = (id: Tab, label: string) => (
    <button key={id} onClick={() => setTab(id)}
      className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
        tab === id
          ? "bg-accent-subtle border-accent/40 text-accent"
          : "bg-bg-surface border-border text-text-secondary hover:bg-bg-hover"
      }`}>{label}</button>
  );

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {tabBtn("roster", "月排班")}
        {tabBtn("shifts", "班別設定")}
        {tabBtn("swaps", "換班單")}
        {tabBtn("hours", "月時數")}
      </div>

      {tab === "roster" && <RosterCalendar />}
      {tab === "swaps" && <SwapRequests />}
      {tab === "hours" && <MonthlyHours />}

      {tab === "shifts" && <>
      <PageHeader
        title="班別設定"
        description="班別設定（時間區段、上班日、人力）。"
        actions={
          <>
            <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
            </button>
            {canWrite && (
              <button data-tour-id="schedules.new" onClick={() => setEditing({ mode: "new" })} className="btn-primary text-sm">
                <Plus className="h-4 w-4" /> 新增班別
              </button>
            )}
          </>
        }
      />
      {state.status === "loading" && <ListSkeleton />}
      {state.status === "error" && (
        <EmptyState icon={ServerCrash} title="班別載入失敗" description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />
      )}
      {state.status === "ready" && state.rows.length === 0 && (
        <EmptyState icon={CalendarDays} title="這個單位還沒有班別" description="建立第一個班別後，就可以排定每週的巡邏排班。"
          action={canWrite ? <button data-tour-id="schedules.new" className="btn-primary text-sm" onClick={() => setEditing({ mode: "new" })}>新增班別</button> : undefined} />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="table-header w-32">班別</th>
                <th className="table-header w-28">時間</th>
                {DAYS.map((d) => (<th key={d} className="table-header text-center">{DAY_LABEL[d]}</th>))}
                <th className="table-header w-16 text-right">人力</th>
                <th className="table-header w-20 text-right">狀態</th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((s) => (
                <tr key={s.id} className="table-row cursor-pointer" onClick={() => setEditing({ mode: "edit", shift: s })}>
                  <td className="table-cell font-medium text-text-primary">{s.name}</td>
                  <td className="table-cell font-mono tabular-nums text-text-secondary">{s.start_time}–{s.end_time}</td>
                  {DAYS.map((_, i) => (
                    <td key={i} className="table-cell text-center">
                      <span className={maskHas(s.days_mask, i) ? "inline-block h-2.5 w-2.5 rounded-full bg-accent" : "inline-block h-2.5 w-2.5 rounded-full bg-bg-raised"} />
                    </td>
                  ))}
                  <td className="table-cell text-right text-text-secondary"><span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{s.officer_count}</span></td>
                  <td className="table-cell text-right"><span className={STATUS_BADGE[s.status]}>{STATUS_LABEL[s.status]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.mode === "edit" ? `編輯 ${editing.shift.name}` : "新增班別"}
        description="班別名稱、時間區段、上班日與人力"
      >
        {editing && (
          <ShiftForm
            mode={editing.mode}
            shift={editing.mode === "edit" ? editing.shift : undefined}
            tenantId={tenantParam}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); void load(); }}
            canArchive={canWrite}
          />
        )}
      </Drawer>
      </>}
    </>
  );
}

function ShiftForm({ mode, shift, tenantId, onClose, onSaved, canArchive }: {
  mode: "new" | "edit"; shift?: ShiftRow; tenantId: string | null;
  onClose: () => void; onSaved: () => void; canArchive: boolean;
}): React.ReactElement {
  const [name, setName] = useState(shift?.name ?? "");
  const [startTime, setStartTime] = useState(shift?.start_time ?? "09:00");
  const [endTime, setEndTime] = useState(shift?.end_time ?? "17:00");
  const [mask, setMask] = useState(shift?.days_mask ?? 0b0011111);
  const [count, setCount] = useState(String(shift?.officer_count ?? 1));
  const [status, setStatus] = useState<ShiftStatus>(shift?.status ?? "active");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body = {
        name: name.trim(), start_time: startTime, end_time: endTime,
        days_mask: mask, officer_count: Math.max(1, Math.min(999, Number(count) || 1)), status,
      };
      if (mode === "new") await createShift(body, tenantId);
      else if (shift) await patchShift(shift.id, body, tenantId);
      onSaved();
    } catch (e) { setErr(friendlyShiftsError(e)); } finally { setBusy(false); }
  };
  const dlg = useConfirmDialog();
  const archive = async () => {
    if (!shift) return;
    if (!(await dlg.confirm(`確定要停用「${shift.name}」？`, { destructive: true, okLabel: "停用" }))) return;
    setBusy(true); setErr(null);
    try { await archiveShift(shift.id, tenantId); onSaved(); }
    catch (e) { setErr(friendlyShiftsError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Field label="班別名稱" required><input data-tour-id="schedules.field.name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="例如：夜班 A" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="開始時間" required><input className="input font-mono" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} /></Field>
        <Field label="結束時間" required><input className="input font-mono" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} /></Field>
      </div>
      <Field label="上班日">
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d, i) => (
            <button key={d} type="button" onClick={() => setMask(maskToggle(mask, i))}
              className={`text-xs rounded-md px-3 py-1.5 border transition-colors ${
                maskHas(mask, i)
                  ? "bg-accent-subtle border-accent/40 text-accent"
                  : "bg-bg-surface border-border text-text-secondary hover:bg-bg-hover"
              }`}>{DAY_LABEL[d]}</button>
          ))}
        </div>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="人力配置" hint="每班需要的人數"><input className="input font-mono" type="number" min="1" max="999" value={count} onChange={(e) => setCount(e.target.value)} /></Field>
        <Field label="狀態">
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as ShiftStatus)}>
            <option value="active">啟用</option><option value="inactive">停用</option>
          </select>
        </Field>
      </div>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex flex-row items-center justify-between gap-2">
        <div>{mode === "edit" && shift && canArchive && (
          <button className="btn-danger text-sm" onClick={() => void archive()} disabled={busy}><Trash2 className="h-4 w-4" /> 停用</button>
        )}</div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          <button data-tour-id="schedules.save" onClick={() => void save()} className="btn-primary text-sm" disabled={busy || !name.trim() || mask === 0}>{busy ? "儲存中…" : "儲存"}</button>
        </div>
      </div>
    </div>
  );
}

function ListSkeleton(): React.ReactElement {
  return (
    <div className="card overflow-hidden">
      <div className="p-4"><Skeleton className="h-9 w-72" /></div>
      <div className="divide-y divide-border">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 p-4">
            <Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-24" />
            {[0, 1, 2, 3, 4, 5, 6].map((j) => <Skeleton key={j} className="h-2.5 w-2.5 rounded-full" />)}
          </div>
        ))}
      </div>
    </div>
  );
}
